// src/pages/ZeroDayMethodPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ZeroDayMethodHeader from '../components/ZeroDayMethod/ZeroDayMethodHeader';
import ServerInfoCard from '../components/Shared/ServerInfoCard';
import ProcessStatusDisplay from '../components/Shared/ProcessStatusDisplay';
import PageTransition from '../components/Shared/PageTransition';
// import LegalDisclaimer from '../components/ZeroDayMethod/LegalDisclaimer'; // Can be added back if needed
import AiConfiguration from '../components/ZeroDayMethod/AiConfiguration';
import AiPerformance from '../components/ZeroDayMethod/AiPerformance';
import AiProgress from '../components/ZeroDayMethod/AiProgress';
// import HowItWorksSection from '../components/ZeroDayMethod/HowItWorksSection'; // Can be added back
import AiReportDisplay from '../components/ZeroDayMethod/AiReportDisplay'; // New

import { motion, AnimatePresence } from 'framer-motion';
import useWebSocket from '../hooks/useWebSocket';
import { useAuth } from '../contexts/AuthContext';
import {
    WS_ZERO_DAY_TYPES,
    WS_ZERO_DAY_TOPICS,
    getZeroDayAppEndpoint,
    getZeroDayUserQueueTopic,
    ZERO_DAY_CONSOLE_TYPES // Import new console types
} from '../config/wsZeroDayConfig';
import { useServerConnection } from '../contexts/ServerConnectionContext';
import { handleWebSocketError } from '../utils/websocketErrorHandler';
import { useNotifications } from '../contexts/NotificationContext';

// Default AI Configuration
const defaultAiConfig = {
    analysisDepth: 3, // 1-5
    analysisFocus: 'balanced', // 'balanced', 'plugins', 'config', 'network', 'auth'
    aiModel: 'zerodai-v3.5-sim', // 'zerodai-v3.5-sim', 'zerodai-v4.0-alpha'
    connectionType: 'proxy_sim', // 'direct_sim', 'proxy_sim', 'vpn_sim'
    stealthMode: true,
};

// Default AI Performance Data (can be updated by backend if needed)
const defaultAiPerformanceData = {
    successRate: 48, // Adjusted for simulation
    detectionRisk: "Medium",
    avgAnalysisTime: "2m 45s",
    modelVersion: "v3.5.2-sim",
    capabilities: [
      { name: "Plugin Analysis", enabled: true },
      { name: "Config Scanning", enabled: true },
      { name: "Version Detection", enabled: true },
      { name: "Exploit Matching", enabled: true },
      { name: "Adaptive Learning", enabled: true },
      { name: "Report Generation", enabled: true },
      { name: "Network Path Analysis", enabled: true },
      { name: "User Behavior Simulation", enabled: false },
    ]
};

// Default AI Progress Data
const initialAiProgressData = {
  overallProgress: 0,
  currentTask: { icon: 'fas fa-hourglass-start', title: 'Awaiting Initialization', description: 'Connect to a server and start analysis.', progress: 0, eta: 'N/A' },
  taskList: [], // Task list will be populated by the backend
  isThinking: false,
};


const ZeroDayMethodPage = ({ openModal }) => {
    const { isAuthenticated } = useAuth();
    const { isConnected: isWebSocketConnected, sendMessage, subscribe } = useWebSocket();
    const { serverInfo, isConnecting, isConnectedToServer, disconnectFromServer } = useServerConnection();
    const subscriptions = useRef({ status: null, progress: null, error: null });
    const { addNotification } = useNotifications();

    // --- State ---
    const [aiConfig, setAiConfig] = useState(defaultAiConfig);
    const [aiPerformanceData, _setAiPerformanceData] = useState(defaultAiPerformanceData); // Performance data is static for now
    const [aiProgressData, setAiProgressData] = useState(initialAiProgressData);

    const [attackState, setAttackState] = useState({
        // 'idle', 'connecting_server', 'configuring_ai', 'analyzing', 'stopping_analysis', 'completed_report', 'rcon_active', 'error'
        status: 'idle',
        message: 'Ready to analyze.',
        isLoading: false, // True for analyzing, stopping, server_connecting
    });
    const [reportData, setReportData] = useState(null);
    const [showReportModal, setShowReportModal] = useState(false);
    const [rconEnabled, setRconEnabled] = useState(false);

const [consoleLines, setConsoleLines] = useState([
        { type: ZERO_DAY_CONSOLE_TYPES.SYSTEM, text: 'Zero-Day AI Module Initialized. Connect to a server.', timestamp: Date.now() },
    ]);
    const [pageError, setPageError] = useState(null); // Page-level error distinct from attackState.message for UI

    // --- Refs for Stable Callbacks ---
    const aiConfigRef = useRef(aiConfig);
    const attackStateRef = useRef(attackState);
    const isOperationRunningRef = useRef(false); // Simplified ref for "is any operation active"
    const rconEnabledRef = useRef(rconEnabled);

    useEffect(() => { aiConfigRef.current = aiConfig; }, [aiConfig]);
    useEffect(() => {
        attackStateRef.current = attackState;
        isOperationRunningRef.current = ['analyzing', 'rcon_active', 'stopping_analysis'].includes(attackState.status);
    }, [attackState]);
    useEffect(() => { rconEnabledRef.current = rconEnabled; }, [rconEnabled]);

    // --- Derived State ---
    const toolsDisabled = !isWebSocketConnected || !isConnectedToServer || isConnecting || attackState.isLoading || ['analyzing', 'stopping_analysis', 'rcon_active'].includes(attackState.status);

    // --- Console Helper ---
const addConsoleLine = useCallback((type, content) => {
        const messageContent = (typeof content === 'object' ? JSON.stringify(content) : String(content)) || '';
        setConsoleLines(prev => [...prev, { type, text: messageContent, timestamp: Date.now() }].slice(-200));
    }, []);

    useEffect(() => {
        // Effect for server connection changes
        if (!isConnectedToServer && !isConnecting) { // No server connection and not trying to connect
            if (attackStateRef.current.status !== 'idle') { // If was doing something, reset
                 addConsoleLine(ZERO_DAY_CONSOLE_TYPES.SYSTEM, 'Target server disconnected. Resetting AI state.');
                 setAttackState({ status: 'idle', message: 'Connect to server.', isLoading: false });
                 setAiProgressData(initialAiProgressData);
                 setRconEnabled(false);
                 setReportData(null);
                 setShowReportModal(false);
            }
        } else if (isConnectedToServer && (attackStateRef.current.status === 'idle' || attackStateRef.current.status === 'connecting_server')) {
            // Server just connected, and we were idle or waiting for server connection
            addConsoleLine(ZERO_DAY_CONSOLE_TYPES.SUCCESS, `Connected to ${serverInfo?.ip}. AI ready for analysis configuration.`);
            setAttackState({ status: 'configuring_ai', message: 'Ready to configure AI.', isLoading: false });
            setAiProgressData(prev => ({ // Ensure progress is reset for new server
                ...initialAiProgressData,
                currentTask: { icon: 'fas fa-play-circle', title: 'Ready for Analysis', description: 'Configure AI and start the process.', progress: 0, eta: 'N/A' }
            }));
        }
    }, [isConnectedToServer, isConnecting, serverInfo?.ip, addConsoleLine]);


    // --- WebSocket Handlers ---
// --- WebSocket Handlers ---
    const handleStatusUpdate = useCallback((message) => {
        console.log('%cWS ZeroDay STATUS:', 'color: #ff3cb4;', message);
        setPageError(null);

        const messageType = message.type;
        const payload = message.data;

        switch (messageType) {
            case WS_ZERO_DAY_TYPES.CONNECTION_STATUS: // From ServerInfoCard context, not directly used by ZeroDay page's own WS messages
                 // This case might be redundant if ServerConnectionContext handles global server connection state
                if (payload.success && payload.serverInfo) {
                    addConsoleLine(ZERO_DAY_CONSOLE_TYPES.SYSTEM, `ZeroDay AI: Confirmed WebSocket connection for server ${payload.serverInfo.ip}.`);
                    if (attackStateRef.current.status === 'idle' || attackStateRef.current.status === 'connecting_server') {
                         setAttackState({ status: 'configuring_ai', message: 'Ready for AI analysis.', isLoading: false });
                    }
                } else if (!payload.success) {
                    addConsoleLine(ZERO_DAY_CONSOLE_TYPES.WARNING, `ZeroDay AI: Server connection issue reported: ${payload.error || 'Unknown'}`);
                    // Let the ServerConnectionContext handle the primary disconnect, this page will react via its useEffect
                }
                break;

case WS_ZERO_DAY_TYPES.EXECUTION_STARTED:
                setAttackState({ status: 'analyzing', message: payload.message || 'AI analysis initiated...', isLoading: true });
                setReportData(null); setShowReportModal(false); setRconEnabled(false);
                
                // Use taskList from payload if available, otherwise use an empty list
                const newTaskList = payload.taskList && payload.taskList.length > 0
                    ? payload.taskList.map(t => ({ ...t, status: 'pending', progress: 0 })) // Ensure status/progress reset
                    : []; // If backend doesn't send taskList, it defaults to empty

                setAiProgressData({ // Construct fresh AI progress state for the new execution
                    taskList: newTaskList,
                    overallProgress: 0,
                    currentTask: { icon: 'fas fa-cogs', title: 'Initializing AI', description: payload.message || 'Preparing analysis core...', progress: 0, eta: 'Calculating...' },
                    isThinking: true,
                });
                addConsoleLine(ZERO_DAY_CONSOLE_TYPES.SYSTEM, `AI Analysis Started: ${payload.message || 'Initiating deep scan...'}`);
                break;

            case WS_ZERO_DAY_TYPES.EXECUTION_COMPLETED:
                 const rconGranted = payload.rconAccessGranted || false;
                 setAttackState({
                     status: rconGranted ? 'rcon_active' : 'completed_report',
                     message: payload.message || (rconGranted ? 'AI analysis successful. RCON Active.' : 'AI analysis complete. View report.'),
                     isLoading: false,
                 });
                 setReportData(payload.reportData || {});
                 setRconEnabled(rconGranted);
                 // Automatically show report if RCON not granted and report exists
                 if (!rconGranted && payload.reportData && Object.keys(payload.reportData).length > 0) {
                    setShowReportModal(true);
                 }

                 setAiProgressData(prev => ({
                    ...prev, // Keep task list as is (should be all completed by now)
                    overallProgress: 100,
                    currentTask: { icon: rconGranted ? 'fas fa-terminal' : (payload.reportData ? 'fas fa-file-alt' : 'fas fa-check-circle'), title: rconGranted ? 'RCON Access Granted' : 'Analysis Complete', description: payload.message, progress: 100, eta: 'Done' },
                    isThinking: false,
                }));
                 addConsoleLine(
                     rconGranted ? ZERO_DAY_CONSOLE_TYPES.SUCCESS : ZERO_DAY_CONSOLE_TYPES.SYSTEM,
                     `AI Analysis Completed: ${payload.message || (rconGranted ? 'RCON access granted!' : 'Report generated.')}`
                 );
                 if (payload.reportData) {
                      addConsoleLine(ZERO_DAY_CONSOLE_TYPES.INFO, `Report Summary: ${payload.reportData.overallAssessment || 'See full report.'}`);
                 }
                 if (rconGranted) {
                    addConsoleLine(ZERO_DAY_CONSOLE_TYPES.SUCCESS, "RCON console is now active. Type 'help' for commands.");
                 }
                break;

            case WS_ZERO_DAY_TYPES.CURRENT_STATE:
                addConsoleLine(ZERO_DAY_CONSOLE_TYPES.SYSTEM, `Received current state from server for ZeroDay AI.`);
                if (payload.serverInfo) { // If backend confirms server context for the state
                    if (payload.operation) { // An operation is active
                        const op = payload.operation;
                        // Map backend 'executing' to frontend 'analyzing'
                        const opStatus = op.status === 'executing' ? 'analyzing' : op.status;
                        const isLoading = opStatus === 'analyzing'; // isLoading true if analyzing
                        const isRconActive = op.rconAccessGranted || false; // Check if RCON was granted

                        setAttackState({
                            status: isRconActive ? 'rcon_active' : opStatus,
                            message: op.message || `Reconnected. Current state: ${opStatus}`,
                            isLoading: isLoading,
                        });
                        setRconEnabled(isRconActive);

                        if (op.progressData) { // Restore AI progress data
                            setAiProgressData(op.progressData);
                        } else { // If no progress data, but operation is active, set to a minimal loading/inconsistent state
                            setAiProgressData({
                                ...initialAiProgressData, // Use minimal base
                                currentTask: { icon: 'fas fa-sync-alt fa-spin', title: 'Synchronizing State', description: 'Loading detailed progress from server...', progress: 0, eta: '...' },
                                isThinking: attackStateRef.current.isLoading,
                            });
                            addConsoleLine(ZERO_DAY_CONSOLE_TYPES.WARNING, "Progress data missing in CURRENT_STATE from backend for active operation. Awaiting next update.");
                        }
                        if (op.consoleLines) setConsoleLines(op.consoleLines); // Restore console history

                        if(isRconActive) addConsoleLine(ZERO_DAY_CONSOLE_TYPES.SUCCESS, "Reconnected to active RCON session.");
                        else if(isLoading) addConsoleLine(ZERO_DAY_CONSOLE_TYPES.SYSTEM, "Reconnected to ongoing AI analysis.");

                    } else { // No operation active on backend for this user + server context
                        setAttackState({ status: 'configuring_ai', message: 'Ready for AI analysis.', isLoading: false });
                        setAiProgressData(initialAiProgressData);
                        setRconEnabled(false);
                        setReportData(null);
                        setShowReportModal(false);
                        addConsoleLine(ZERO_DAY_CONSOLE_TYPES.SYSTEM, `State received: No active AI operation. Ready to configure for ${payload.serverInfo.ip}.`);
                    }
                } else { // No server context in state (implies fully idle)
                    setAttackState({ status: 'idle', message: 'Connect to a server for analysis.', isLoading: false });
                    addConsoleLine(ZERO_DAY_CONSOLE_TYPES.INFO, 'State update: Not connected to any server for ZeroDay AI.');
                    setAiProgressData(initialAiProgressData); // Reset to minimal initial state
                    setRconEnabled(false);
                    setShowReportModal(false);
                }
                break;

            default:
                console.warn('Unhandled ZeroDay status message type:', messageType, payload);
        }
    }, [addConsoleLine]);

    const handleProgressUpdate = useCallback((message) => {
        const messageType = message.type;
        const payload = message.data;

        if (messageType === WS_ZERO_DAY_TYPES.CONSOLE_LINE) {
            // payload is { type: string (console style), content: string, progressUpdate?: ZeroDayTaskUpdateDto }
            addConsoleLine(payload.type || ZERO_DAY_CONSOLE_TYPES.INFO, payload.content);
            
            if (payload.progressUpdate) {
                const progress = payload.progressUpdate;
                console.log('Legacy progressUpdate in CONSOLE_LINE:', progress);
                setAiProgressData(prev => ({
                    ...prev,
                    overallProgress: progress.overallProgress !== undefined ? progress.overallProgress : prev.overallProgress,
                    currentTask: {
                        ...prev.currentTask,
                        ...(progress.currentGlobalTaskTitle && { title: progress.currentGlobalTaskTitle }),
                        ...(progress.currentGlobalTaskDescription && { description: progress.currentGlobalTaskDescription }),
                        ...(progress.currentGlobalTaskIcon && { icon: progress.currentGlobalTaskIcon }),
                        eta: progress.eta || prev.currentTask.eta,
                    },
                    taskList: prev.taskList.map(task =>
                        task.id === progress.taskId
                        ? { ...task, status: progress.taskStatus || task.status, progress: progress.taskProgress !== undefined ? progress.taskProgress : task.progress, description: progress.taskDescription || task.description, icon: progress.taskIcon || task.icon }
                        : task
                    ),
                    isThinking: (progress.overallProgress !== undefined && progress.overallProgress < 100) || prev.isThinking,
                }));
            }
        } else if (messageType === WS_ZERO_DAY_TYPES.AI_TASK_UPDATE) {
            // payload is ZeroDayTaskUpdateDto
            console.log('AI_TASK_UPDATE received:', payload);
            setAiProgressData(prev => {
                const newTaskList = prev.taskList.map(task =>
                    task.id === payload.taskId
                    ? { ...task, status: payload.taskStatus || task.status, description: payload.taskDescription || task.description, progress: payload.taskProgress !== undefined ? payload.taskProgress : task.progress, icon: payload.taskIcon || task.icon }
                    : task
                );
                const activeTaskFromList = newTaskList.find(t => t.status === 'active');

                return {
                    ...prev,
                    overallProgress: payload.overallProgress !== undefined ? payload.overallProgress : prev.overallProgress,
                    currentTask: { 
                         icon: payload.currentGlobalTaskIcon || (activeTaskFromList ? activeTaskFromList.icon : prev.currentTask.icon),
                         title: payload.currentGlobalTaskTitle || (activeTaskFromList ? activeTaskFromList.title : prev.currentTask.title),
                         description: payload.currentGlobalTaskDescription || (activeTaskFromList ? activeTaskFromList.description : prev.currentTask.description),
                         progress: (activeTaskFromList && payload.currentGlobalTaskTitle === activeTaskFromList.title) ? activeTaskFromList.progress : (payload.overallProgress || 0),
                         eta: payload.eta || prev.currentTask.eta,
                    },
                    taskList: newTaskList,
                    isThinking: (payload.overallProgress !== undefined && payload.overallProgress < 100),
                };
            });
        } else if (messageType === WS_ZERO_DAY_TYPES.OPERATION_STATUS) {
            // payload is { message: string }
            addConsoleLine(ZERO_DAY_CONSOLE_TYPES.SYSTEM, `Status Update: ${payload.message}`);
            if (attackStateRef.current.status === 'analyzing' || attackStateRef.current.status === 'stopping_analysis') {
                 setAttackState(prev => ({ ...prev, message: payload.message }));
            }
        } else {
            console.warn('Unhandled ZeroDay progress message type:', messageType, payload);
        }
    }, [addConsoleLine]);

    const handleErrorUpdate = useCallback((errorDataMessage) => { // errorDataMessage is { type: "ERROR_TYPE", data: { message: "...", ...} }
        console.error('%cWS ZeroDay ERROR:', 'color: red;', errorDataMessage);
        // errorDataMessage.data contains the actual error payload { message, isFatal, code, context }
        const errorPayload = errorDataMessage.data || { message: "Unknown ZeroDay error structure." };
        
        const message = handleWebSocketError(errorPayload, null, openModal, addNotification, { defaultTitle: 'Zero-Day AI Error', moduleName: 'Zero-Day AI' });
        setPageError(message);
        addConsoleLine(ZERO_DAY_CONSOLE_TYPES.ERROR, `Error: ${message} (Code: ${errorPayload?.code || 'N/A'})`);
        setAttackState(prev => ({ ...prev, status: 'error', message: `Error: ${message}`, isLoading: false }));
        setAiProgressData(prev => ({ ...prev, isThinking: false, currentTask: { ...prev.currentTask, title: 'Error Occurred', icon: 'fas fa-exclamation-triangle' } }));
        setRconEnabled(false);
    }, [addConsoleLine, openModal, addNotification]);

    // --- WebSocket Subscriptions ---
    useEffect(() => {
        if (!isWebSocketConnected || !isAuthenticated) {
            Object.values(subscriptions.current).forEach(unsubscribe => unsubscribe?.());
            subscriptions.current = { status: null, progress: null, error: null };
            if (isOperationRunningRef.current) { // If an operation was active, show WS lost message
                addConsoleLine(ZERO_DAY_CONSOLE_TYPES.ERROR, 'WebSocket connection lost. Current operation might be interrupted. Attempting to restore state...');
            }
            return;
        }

        console.log("Subscribing to ZeroDay AI topics...");
        const statusSub = subscribe(getZeroDayUserQueueTopic(WS_ZERO_DAY_TOPICS.STATUS), handleStatusUpdate);
        const progressSub = subscribe(getZeroDayUserQueueTopic(WS_ZERO_DAY_TOPICS.PROGRESS), handleProgressUpdate);
        const errorSub = subscribe(getZeroDayUserQueueTopic(WS_ZERO_DAY_TOPICS.ERROR), handleErrorUpdate);
        subscriptions.current = { status: statusSub, progress: progressSub, error: errorSub };

        // Request current state after a short delay to ensure subscriptions are established
        const timerId = setTimeout(() => {
             if (isWebSocketConnected && isAuthenticated && isConnectedToServer) { // Only get state if connected to a server
                console.log("Requesting current ZeroDay AI state...");
                sendMessage(getZeroDayAppEndpoint(WS_ZERO_DAY_TOPICS.GET_STATE), { ip: serverInfo.ip }); // Send target IP for context
             } else if (isWebSocketConnected && isAuthenticated && !isConnectedToServer && attackStateRef.current.status !== 'idle') {
                // WS connected, Auth OK, but no server -> reset local state if it wasn't idle
                setAttackState({ status: 'idle', message: 'Connect to server.', isLoading: false });
                setAiProgressData(initialAiProgressData);
             }
        }, 700); // Increased delay slightly

        return () => {
            console.log("Unsubscribing from ZeroDay AI topics...");
            Object.values(subscriptions.current).forEach(unsubscribe => unsubscribe?.());
            subscriptions.current = { status: null, progress: null, error: null };
            clearTimeout(timerId);
        };
    }, [isWebSocketConnected, isAuthenticated, subscribe, sendMessage, handleStatusUpdate, handleProgressUpdate, handleErrorUpdate, addConsoleLine, isConnectedToServer, serverInfo?.ip ]);

    // --- UI Action Handlers ---
    const handleAiConfigChange = useCallback((name, value) => {
        setAiConfig(prev => ({ ...prev, [name]: value }));
    }, []);

const handleStartAiAnalysis = useCallback(() => {
        if (!isConnectedToServer || !serverInfo?.ip) {
             setPageError("Cannot start: Not connected to a target server.");
             addConsoleLine(ZERO_DAY_CONSOLE_TYPES.ERROR, "AI Analysis failed: Target server not connected.");
             return;
        }
        if (isOperationRunningRef.current) {
             addConsoleLine(ZERO_DAY_CONSOLE_TYPES.WARNING, "Cannot start: An AI operation is already in progress.");
             return;
        }

        // Open the maintenance modal instead of starting the AI analysis
        openModal('methodMaintenance');

        // Original logic to start AI analysis (now commented out):
        // console.log(`Starting ZeroDay AI Analysis for ${serverInfo.ip} with config:`, aiConfigRef.current);
        // setPageError(null);
        // setAttackState({ status: 'analyzing', message: 'Initiating AI analysis...', isLoading: true });
        // setAiProgressData({ // Set to an "initiating" state, backend EXECUTION_STARTED will provide actual plan
        //     taskList: [], // Task list will come from EXECUTION_STARTED payload
        //     overallProgress: 0,
        //     isThinking: true,
        //     currentTask: { icon: 'fas fa-cogs', title: 'Initializing AI', description: 'Booting up analysis core...', progress: 0, eta: '...' }
        // });
        // addConsoleLine(ZERO_DAY_CONSOLE_TYPES.SYSTEM, `Initiating AI analysis on ${serverInfo.ip} with model: ${aiConfigRef.current.aiModel}`);
        //
        // sendMessage(getZeroDayAppEndpoint(WS_ZERO_DAY_TOPICS.EXECUTE), {
        //     ip: serverInfo.ip,
        //     vectorId: 0, // vectorId is less relevant for AI sim, can be default or from config
        //     options: { aiConfig: aiConfigRef.current }
        // });
    }, [isConnectedToServer, serverInfo?.ip, addConsoleLine, openModal]); // Added openModal to dependencies

    const handleStopOperation = useCallback(() => {
         if (!isOperationRunningRef.current || !serverInfo?.ip || !isWebSocketConnected) {
              addConsoleLine(ZERO_DAY_CONSOLE_TYPES.WARNING, "Cannot stop: No analysis running or WebSocket disconnected.");
              return;
         }
         console.log("Sending stop AI analysis/RCON request...");
         setAttackState(prev => ({ ...prev, status: 'stopping_analysis', message: 'Stopping current operation...', isLoading: true }));
         setAiProgressData(prev => ({ ...prev, isThinking: false, currentTask: { ...prev.currentTask, title: 'Stopping Operation...', icon: 'fas fa-stop-circle'} }));
         addConsoleLine(ZERO_DAY_CONSOLE_TYPES.WARNING, 'Attempting to stop current AI operation...');
         sendMessage(getZeroDayAppEndpoint(WS_ZERO_DAY_TOPICS.STOP), { ip: serverInfo.ip });
    }, [serverInfo?.ip, isWebSocketConnected, sendMessage, addConsoleLine]);

    const processActions = [
        ...(['analyzing', 'rcon_active', 'stopping_analysis'].includes(attackState.status)
            ? [{
                  label: attackState.status === 'rcon_active' ? 'Stop RCON Session' : 'Stop Analysis',
                  onClick: handleStopOperation,
                  icon: 'fa-stop',
                  primary: true,
                  disabled: attackState.status === 'stopping_analysis',
                  loadingLabel: 'Stopping...',
                  activeClass: 'bg-gradient-to-r from-accent-red to-accent-orange text-white hover:opacity-90',
                  loadingClass: 'bg-gradient-to-r from-accent-red/70 to-accent-orange/70 text-white opacity-70 cursor-wait',
              }]
            : [
                {
                    label: 'Start AI Analysis',
                    onClick: handleStartAiAnalysis,
                    icon: 'fa-brain',
                    primary: true,
                    disabled: toolsDisabled,
                    loadingLabel: 'Initiating...',
                    activeClass: 'bg-gradient-to-r from-accent-purple to-accent-pink text-white hover:opacity-90',
                }
              ]
        )
    ];

    const handleSendCommand = useCallback((command) => {
        if (!rconEnabledRef.current || attackStateRef.current.status !== 'rcon_active') {
            addConsoleLine(ZERO_DAY_CONSOLE_TYPES.ERROR, "RCON is not active.");
            return;
        }
        if (!command.trim()) return;
        // addConsoleLine(ZERO_DAY_CONSOLE_TYPES.RCON_REQUEST, `> ${command}`); // Backend will echo this
        sendMessage(getZeroDayAppEndpoint(WS_ZERO_DAY_TOPICS.RCON_COMMAND), { command });
    }, [sendMessage, addConsoleLine]);


    const getStatusColor = () => {
        switch (attackState.status) {
            case 'analyzing': return 'text-accent-pink';
            case 'stopping_analysis': return 'text-accent-orange';
            case 'completed_report': return 'text-accent-green';
            case 'rcon_active': return 'text-accent-green'; // Changed from text-accent-teal to text-accent-green
            case 'error': return 'text-accent-red';
            case 'configuring_ai': return 'text-gray-400';
            case 'idle': return isConnectedToServer ? 'text-gray-400' : 'text-accent-yellow';
            default: return 'text-gray-500';
        }
    };

    return (
        <PageTransition>
            <div className="p-6 lg:p-8">
                <ZeroDayMethodHeader />
                <ServerInfoCard />

                 {pageError && (
                    <motion.div
                         initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                         className="mb-4 p-3 bg-red-900/30 border border-accent-red/50 rounded-lg text-accent-red text-sm"
                    >
                         <i className="fas fa-exclamation-triangle mr-2"></i>
                         <strong>Error:</strong> {pageError}
                     </motion.div>
                 )}

                <AnimatePresence>
                    {( // Show content if connected AND not in pure 'idle' state
                        <motion.div
                            key="zero-day-ai-content"
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.4 }} layout
                        >
                            {/* AI Configuration and Performance */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                                <div className="lg:col-span-1">
                                    <AiConfiguration
                                        config={aiConfig}
                                        onChange={handleAiConfigChange}
                                        isLoading={attackState.isLoading || isConnecting || ['analyzing', 'stopping_analysis'].includes(attackState.status) }
                                        isConnected={isConnectedToServer}
                                    />
                                </div>
                                <div className="lg:col-span-2">
                                    <AiPerformance
                                        performanceData={aiPerformanceData}
                                        isLoading={isConnecting}
                                    />
                                </div>
                            </div>

                            {/* Process Console and AI Progress */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                                <div className="lg:col-span-2 h-full">
                                    <ProcessStatusDisplay
                                        lines={consoleLines}
                                        status={attackState.message || attackState.status.replace(/_/g, ' ')} // Format status message
                                        statusColor={getStatusColor()}
                                        isLoading={attackState.isLoading && attackState.status === 'analyzing'} // Spinner only for 'analyzing'
                                        actions={processActions}
                                        consoleTitle="Zero-Day AI Operation Log"
                                        isConsoleInputEnabled={rconEnabled && attackState.status === 'rcon_active'}
                                        onSendCommand={handleSendCommand}
                                        consoleMaxHeight="h-[450px]"
                                        showHeader={true}
                                        showProgressBar={false}
                                    />
                                </div>
                                <div className="lg:col-span-1 h-full">
                                   <AiProgress
                                        progressData={aiProgressData}
                                        isLoading={attackState.isLoading || isConnecting || attackState.status === 'analyzing'}
                                    />
                                </div>
                            </div>

                            {/* Attack Results (Report Modal Trigger) */}
                             {attackState.status === 'completed_report' && reportData && Object.keys(reportData).length > 0 && (
                                <div className="my-6 p-6 bg-dark-600 rounded-2xl border border-accent-green/30 text-center">
                                    <h3 className="font-bold text-xl mb-3 text-accent-green">
                                        <i className="fas fa-check-circle mr-2"></i>AI Analysis Complete
                                    </h3>
                                    <p className="text-gray-300 mb-4">{reportData.overallAssessment || "The Zero-Day AI has finished its analysis. A detailed report is available."}</p>
                                    <button
                                        onClick={() => setShowReportModal(true)}
                                        className="px-6 py-3 bg-gradient-to-r from-accent-green to-green-400 text-white font-semibold rounded-lg shadow-lg hover:opacity-90 transition-opacity"
                                    >
                                        <i className="fas fa-file-alt mr-2"></i>View Full Report
                                    </button>
                                </div>
                            )}

{/* RCON Granted Message (if not showing full report immediately) */}
                            {attackState.status === 'rcon_active' && (
                                <div className="my-6 bg-dark-600 rounded-2xl p-6 border border-accent-green shadow-lg shadow-accent-green/20 transform-gpu hover:scale-[1.01] transition-medium">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="font-bold text-xl text-accent-green flex items-center">
                                            <i className="fas fa-terminal mr-3 text-2xl animate-pulse"></i>AI Achieved RCON Access!
                                        </h3>
                                        <span className="text-sm bg-accent-green/20 text-accent-green px-3 py-1 rounded-full font-semibold">Live Control</span>
                                    </div>
                                    <p className="text-gray-300 mb-3 text-sm">
                                        The Zero-Day AI has successfully gained RCON access to <span className="font-semibold text-white">{serverInfo?.ip || 'the target server'}</span>.
                                        The console above is now active for command execution.
                                    </p>
                                    {reportData?.overallAssessment && (
                                        <p className="font-mono text-xs bg-dark-700 p-3 rounded-lg border border-dark-400 mb-4">
                                            <span className="text-gray-400 block">AI Assessment: <span className="text-accent-teal">{reportData.overallAssessment}</span></span>
                                            {reportData.aiModelUsed && <span className="text-gray-400 block mt-1">Model: <span className="text-white">{reportData.aiModelUsed}</span></span>}
                                        </p>
                                    )}
                                     {reportData && Object.keys(reportData).length > 0 && (
                                        <button
                                            onClick={() => setShowReportModal(true)}
                                            className="px-5 py-2.5 bg-accent-blue hover:bg-accent-blue/80 text-white text-sm rounded-lg font-medium transition-colors flex items-center shadow-md hover:shadow-lg btn-hover-effect"
                                        >
                                            <i className="fas fa-file-alt mr-2"></i>View Full Analysis Report
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* <HowItWorksSection /> */}
                            {/* <LegalDisclaimer /> */}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Show Report Modal */}
                <AnimatePresence>
                    {showReportModal && reportData && (
                        <AiReportDisplay reportData={reportData} onClose={() => setShowReportModal(false)} />
                    )}
                </AnimatePresence>

                {/* Placeholder when not connected to a server or fully idle */}
                {/*(!isConnectedToServer && !isConnecting) && attackState.status === 'idle' && (
                    <div className="text-center py-16 text-gray-500 bg-dark-600 rounded-2xl mt-8 border border-dark-400">
                        <motion.div initial={{opacity:0, scale:0.8}} animate={{opacity:1, scale:1}} transition={{delay: 0.2}}>
                            <i className="fas fa-plug text-5xl mb-5 text-gray-600"></i>
                            <p className="text-lg">Connect to a server to activate Zero-Day AI.</p>
                            <p className="text-sm text-gray-600 mt-2">Use the card above to enter the target server address.</p>
                        </motion.div>
                    </div>
                )*/}
                 {/*isConnecting && ( // ServerInfoCard connecting
                     <div className="text-center py-16 text-gray-500 bg-dark-600 rounded-2xl mt-8 border border-dark-400">
                        <motion.div initial={{opacity:0}} animate={{opacity:1}} transition={{delay: 0.1}}>
                             <i className="fas fa-spinner fa-spin text-5xl mb-5 text-accent-purple"></i>
                             <p className="text-lg animate-pulse">Connecting to server...</p>
                         </motion.div>
                     </div>
                 )*/}
            </div>
        </PageTransition>
    );
};

export default ZeroDayMethodPage;